import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  console.warn('⚠️ Supabase env vars not set. Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY in .env.local');
}

const supabase = createClient(SUPABASE_URL || '', SUPABASE_ANON_KEY || '');

// COMMERCIAL-DEMO: hard kill switch for all user-draft cloud writes.
// Demo users' draft preparation must stay strictly on their own machine —
// no Supabase sync, no Firestore sync (see FirebaseService.js counterpart).
const CLOUD_SAVE_DISABLED = true;

// camelCase → snake_case mapping for the drafts table
function toRow(draftData) {
  return {
    id: draftData.id || undefined,
    user_id: draftData.userId || '',
    user_email: draftData.userEmail || '',
    title: draftData.title || '',
    team_name: draftData.teamName || '',
    tournament: draftData.tournament || '',
    stage: draftData.stage || '',
    date: draftData.date || '',
    layout_version: draftData.layoutVersion || null,
    is_public: draftData.isPublic || false,
    scenarios_first_pick: draftData.scenariosFirstPick || [],
    scenarios_second_pick: draftData.scenariosSecondPick || [],
    radiant_heroes: draftData.radiantHeroes || [],
    dire_heroes: draftData.direHeroes || [],
    radiant_drafts: draftData.radiantDrafts || {},
    dire_drafts: draftData.direDrafts || {},
    radiant_picks: draftData.radiantPicks || [],
    dire_picks: draftData.direPicks || [],
    radiant_bans: draftData.radiantBans || [],
    dire_bans: draftData.direBans || [],
    created_at: draftData.createdAt || new Date().toISOString(),
    updated_at: draftData.updatedAt || new Date().toISOString(),
  };
}

// snake_case row → camelCase draft object (full data)
function fromRow(row) {
  return {
    id: row.id,
    title: row.title || '',
    teamName: row.team_name || '',
    tournament: row.tournament || '',
    stage: row.stage || '',
    date: row.date || '',
    layoutVersion: row.layout_version || null,
    isPublic: row.is_public || false,
    userId: row.user_id || '',
    userEmail: row.user_email || '',
    scenariosFirstPick: row.scenarios_first_pick || [],
    scenariosSecondPick: row.scenarios_second_pick || [],
    radiantHeroes: row.radiant_heroes || [],
    direHeroes: row.dire_heroes || [],
    radiantDrafts: row.radiant_drafts || {},
    direDrafts: row.dire_drafts || {},
    radiantPicks: row.radiant_picks || [],
    direPicks: row.dire_picks || [],
    radiantBans: row.radiant_bans || [],
    direBans: row.dire_bans || [],
    updatedAt: row.updated_at || new Date().toISOString(),
    createdAt: row.created_at || new Date().toISOString(),
  };
}

// Lightweight metadata (no scenario JSONB) for RecordViewer list queries
function fromRowMeta(row) {
  return {
    id: row.id,
    title: row.title || '',
    teamName: row.team_name || '',
    tournament: row.tournament || '',
    stage: row.stage || '',
    date: row.date || '',
    layoutVersion: row.layout_version || null,
    isPublic: row.is_public || false,
    userId: row.user_id || '',
    userEmail: row.user_email || '',
    updatedAt: row.updated_at || new Date().toISOString(),
    createdAt: row.created_at || new Date().toISOString(),
    // Scenario counts from separate fields if available
    scenariosFirstPick: [],
    scenariosSecondPick: [],
  };
}

class SupabaseService {
  /**
   * Save (upsert) a draft to Supabase.
   * Returns the draft id.
   */
  async saveDraft(draftData) {
    // COMMERCIAL-DEMO: cloud saving disabled — drafts never leave this machine.
    if (CLOUD_SAVE_DISABLED) {
      console.log('☁️🚫 Supabase saveDraft skipped (commercial-demo: local-only build)');
      return draftData.id || ('local_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8));
    }
    if (!draftData.userId) {
      throw new Error('userId is required to save a draft');
    }

    const now = new Date().toISOString();
    draftData.updatedAt = now;
    if (!draftData.createdAt) {
      draftData.createdAt = now;
    }

    const row = toRow(draftData);

    // If no id, remove it so Postgres generates a UUID or we use addDoc pattern
    // For Supabase we use a text PK - generate one if missing
    if (!row.id) {
      row.id = 'draft_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);
    }

    // Try INSERT first; if duplicate key, fall back to UPDATE
    const { error: insertError } = await supabase
      .from('drafts')
      .insert(row);

    if (insertError) {
      if (insertError.code === '23505') {
        // Row exists — update it instead
        const { error: updateError } = await supabase
          .from('drafts')
          .update(row)
          .eq('id', row.id);
        if (updateError) {
          console.error('❌ Supabase saveDraft update error:', updateError);
          throw new Error(`Supabase update failed (${updateError.code}): ${updateError.message}`);
        }
      } else {
        console.error('❌ Supabase saveDraft insert error:', insertError);
        console.error('❌ Code:', insertError.code, 'Message:', insertError.message, 'Details:', insertError.details);
        throw new Error(`Supabase insert failed (${insertError.code}): ${insertError.message}`);
      }
    }

    return row.id;
  }

  /**
   * Fetch a single draft with full scenario data.
   */
  async getDraft(draftId) {
    if (CLOUD_SAVE_DISABLED) {
      console.log('☁️🚫 Supabase ' + 'getDraft' + ' skipped (commercial-demo: local-only build)');
      return null;
    }
    const { data, error } = await supabase
      .from('drafts')
      .select('*')
      .eq('id', draftId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null; // not found
      console.error('❌ Supabase getDraft error:', error);
      throw error;
    }

    return fromRow(data);
  }

  /**
   * Fetch all drafts for a user (full data including scenarios).
   * Used by DraftContext for loading draft lists.
   */
  async getUserDrafts(userId) {
    if (CLOUD_SAVE_DISABLED) {
      console.log('☁️🚫 Supabase ' + 'getUserDrafts' + ' skipped (commercial-demo: local-only build)');
      return [];
    }
    const { data, error } = await supabase
      .from('drafts')
      .select('*')
      .eq('user_id', userId)
      .order('updated_at', { ascending: false });

    if (error) {
      console.error('❌ Supabase getUserDrafts error:', error);
      throw error;
    }

    return (data || []).map(fromRow);
  }

  /**
   * Delete a draft by id (with user_id safety check).
   */
  async deleteDraft(draftId, userId) {
    if (CLOUD_SAVE_DISABLED) {
      console.log('☁️🚫 Supabase deleteDraft skipped (commercial-demo: local-only build)');
      return true;
    }
    let query = supabase.from('drafts').delete().eq('id', draftId);
    if (userId) {
      query = query.eq('user_id', userId);
    }
    const { error } = await query;
    if (error) {
      console.error('❌ Supabase deleteDraft error:', error);
      throw new Error(`Supabase delete failed (${error.code}): ${error.message}`);
    }
    return true;
  }

  /**
   * Fetch public drafts (excluding the current user's own drafts).
   */
  async getPublicDrafts(excludeUserId) {
    if (CLOUD_SAVE_DISABLED) {
      console.log('☁️🚫 Supabase ' + 'getPublicDrafts' + ' skipped (commercial-demo: local-only build)');
      return [];
    }
    let query = supabase
      .from('drafts')
      .select('*')
      .eq('is_public', true)
      .order('updated_at', { ascending: false });

    if (excludeUserId) {
      query = query.neq('user_id', excludeUserId);
    }

    const { data, error } = await query;

    if (error) {
      console.error('❌ Supabase getPublicDrafts error:', error);
      throw error;
    }

    return (data || []).map(fromRow);
  }

  /**
   * Lightweight paginated query for RecordViewer.
   * Returns only metadata columns — no scenario JSONB blobs.
   *
   * @param {string} userId
   * @param {{ teamSearch?: string, tournamentSearch?: string, page?: number, pageSize?: number }} opts
   * @returns {{ drafts: DraftMeta[], totalCount: number }}
   */
  async getRecordViewerPage(userId, opts = {}) {
    if (CLOUD_SAVE_DISABLED) {
      console.log('☁️🚫 Supabase ' + 'getRecordViewerPage' + ' skipped (commercial-demo: local-only build)');
      return { drafts: [], totalCount: 0 };
    }
    const {
      teamSearch = '',
      tournamentSearch = '',
      page = 1,
      pageSize = 50,
    } = opts;

    const {
      exactTeamName = null,
    } = opts;

    const selectCols = 'id, user_id, user_email, title, team_name, tournament, stage, date, layout_version, is_public, created_at, updated_at, scenarios_first_pick, scenarios_second_pick';

    let query = supabase
      .from('drafts')
      .select(selectCols, { count: 'exact' })
      .eq('user_id', userId);

    if (exactTeamName) {
      query = query.eq('team_name', exactTeamName);
    } else if (teamSearch.trim()) {
      query = query.ilike('team_name', `%${teamSearch.trim()}%`);
    }

    if (tournamentSearch.trim()) {
      query = query.ilike('tournament', `%${tournamentSearch.trim()}%`);
    }

    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    query = query.order('updated_at', { ascending: false }).range(from, to);

    const { data, error, count } = await query;

    if (error) {
      console.error('❌ Supabase getRecordViewerPage error:', error);
      throw error;
    }

    return {
      drafts: (data || []).map(fromRow),
      totalCount: count || 0,
    };
  }

  /**
   * Fetch only the scenario JSONB for a single draft (lazy load on card expand).
   */
  async getDraftScenarios(draftId) {
    if (CLOUD_SAVE_DISABLED) {
      console.log('☁️🚫 Supabase ' + 'getDraftScenarios' + ' skipped (commercial-demo: local-only build)');
      return null;
    }
    const { data, error } = await supabase
      .from('drafts')
      .select('id, scenarios_first_pick, scenarios_second_pick, layout_version')
      .eq('id', draftId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      console.error('❌ Supabase getDraftScenarios error:', error);
      throw error;
    }

    return {
      id: data.id,
      scenariosFirstPick: data.scenarios_first_pick || [],
      scenariosSecondPick: data.scenarios_second_pick || [],
      layoutVersion: data.layout_version || null,
    };
  }

  /**
   * Migrate drafts one-at-a-time to avoid large payload issues.
   * Each draft is upserted individually so scenario JSONB never batches up.
   *
   * @param {object[]} drafts - Array of draft objects in camelCase format
   * @param {function} onProgress - Callback(done, total)
   */
  async migrateBatch(drafts, onProgress) {
    if (CLOUD_SAVE_DISABLED) {
      console.log('☁️🚫 Supabase migrateBatch skipped (commercial-demo: local-only build)');
      return 0;
    }
    let done = 0;

    for (const d of drafts) {
      const row = toRow(d);
      if (!row.id) {
        row.id = 'draft_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);
      }

      // Plain INSERT via Supabase JS client — no Prefer header, no upsert
      // (Supabase is empty on first migration so no duplicate conflicts expected)
      const { error } = await supabase
        .from('drafts')
        .insert(row);

      if (error) {
        // If duplicate, skip it (already migrated)
        if (error.code === '23505') {
          console.warn('⚠️ Draft already in Supabase, skipping:', row.id);
        } else {
          console.error('❌ migrateBatch insert error for draft', row.id, error);
          console.error('❌ Code:', error.code, 'Message:', error.message, 'Details:', error.details);
          throw new Error(`Supabase insert error (${error.code || 'unknown'}): ${error.message}`);
        }
      }

      done += 1;
      if (onProgress) onProgress(done, drafts.length);
    }

    return done;
  }

  /**
   * Diagnose Supabase connectivity — call from browser console:
   *   import supabaseService from './services/SupabaseService'; supabaseService.testWrite();
   */
  async testWrite() {
    if (CLOUD_SAVE_DISABLED) {
      console.log('☁️🚫 Supabase testWrite skipped (commercial-demo: local-only build)');
      return null;
    }
    const testId = 'test_' + Date.now();
    const url = `${SUPABASE_URL}/rest/v1/drafts`;
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
          'Prefer': 'return=minimal',
        },
        body: JSON.stringify({ id: testId, user_id: 'test', title: 'connectivity-test' }),
      });
      const text = await res.text().catch(() => '');
      console.log('testWrite status:', res.status, text);
      if (res.ok) {
        // clean up
        await fetch(`${url}?id=eq.${testId}`, {
          method: 'DELETE',
          headers: { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${SUPABASE_ANON_KEY}` },
        });
        console.log('✅ Supabase write test PASSED');
      }
      return res.status;
    } catch (e) {
      console.error('❌ Supabase write test FAILED:', e);
      return null;
    }
  }

  /**
   * Get distinct team names for a user, sorted by most recent draft.
   * Returns [{teamName, latestUpdatedAt}]
   */
  async getTeamList(userId) {
    if (CLOUD_SAVE_DISABLED) {
      console.log('☁️🚫 Supabase ' + 'getTeamList' + ' skipped (commercial-demo: local-only build)');
      return [];
    }
    const { data, error } = await supabase
      .from('drafts')
      .select('team_name, updated_at')
      .eq('user_id', userId)
      .order('updated_at', { ascending: false });

    if (error) {
      console.error('❌ Supabase getTeamList error:', error);
      return [];
    }

    // Deduplicate by team_name, keeping most recent (first occurrence since sorted desc)
    const seen = new Set();
    const teams = [];
    for (const row of (data || [])) {
      const name = row.team_name || '';
      if (!seen.has(name)) {
        seen.add(name);
        teams.push({ teamName: name, latestUpdatedAt: row.updated_at });
      }
    }
    return teams;
  }

  /**
   * Check if there are any drafts for this user (used to show migration button).
   */
  async getDraftCount(userId) {
    if (CLOUD_SAVE_DISABLED) {
      console.log('☁️🚫 Supabase ' + 'getDraftCount' + ' skipped (commercial-demo: local-only build)');
      return 0;
    }
    const { count, error } = await supabase
      .from('drafts')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', userId);

    if (error) {
      console.error('❌ Supabase getDraftCount error:', error);
      return 0;
    }

    return count || 0;
  }
}

const supabaseService = new SupabaseService();
export default supabaseService;
